import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { getSocket } from '../services/socket';
import { useAccounts } from '../context/AccountContext';

// Banner shown to a copier when one of the traders they follow loses its
// broker connection or fails login/auth. Offers two choices:
//   • Close all positions — closes every copied position from that trader and
//     pauses the relationship (so they aren't re-opened on reconnect).
//   • Keep & wait — dismiss the banner locally and keep positions open.
// Live updates arrive on the `trader:status` socket event; the list is also
// fetched on mount so it survives a page reload.
//
// One banner per RELATIONSHIP: a copier following the same trader from two of
// their own accounts has two independent sets of positions, and "close all"
// must act on exactly the one they clicked.

const DISMISS_PREFIX = 'traderDownDismissed:';
const dismissKey = (a) => `${DISMISS_PREFIX}${a.relationshipId}:${a.since || ''}`;

export default function TraderDownBanner() {
  const { scopeParams } = useAccounts();
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(() => new Set(
    Object.keys(localStorage).filter(k => k.startsWith(DISMISS_PREFIX))
  ));
  const [confirming, setConfirming] = useState(null); // relationshipId awaiting confirm
  const [busy, setBusy] = useState(null);             // relationshipId being closed
  const [done, setDone] = useState({});               // relationshipId -> result message

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/copier/trader-alerts', { params: scopeParams });
      setAlerts(Array.isArray(data) ? data : []);
    } catch (_) { /* leave existing state on transient errors */ }
  }, [scopeParams]);

  useEffect(() => {
    load();
    const socket = getSocket();
    const onStatus = () => load(); // refetch for accurate position counts
    socket?.on('trader:status', onStatus);
    return () => { socket?.off('trader:status', onStatus); };
  }, [load]);

  function keepAndWait(a) {
    const key = dismissKey(a);
    localStorage.setItem(key, '1');
    setDismissed(prev => new Set(prev).add(key));
    setConfirming(null);
  }

  async function closeAll(a) {
    setBusy(a.relationshipId);
    try {
      const { data } = await api.post(`/copier/relationships/${a.relationshipId}/close-all`);
      setDone(prev => ({
        ...prev,
        [a.relationshipId]: data.message || 'Positions closed and copying paused.',
      }));
      load();
    } catch (err) {
      setDone(prev => ({
        ...prev,
        [a.relationshipId]: err.response?.data?.error || 'Failed to close positions — please retry.',
      }));
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  }

  const visible = alerts.filter(a => !dismissed.has(dismissKey(a)));
  if (!visible.length) return null;

  return (
    <>
      {visible.map((a) => {
        const result = done[a.relationshipId];
        const isBusy = busy === a.relationshipId;
        const isConfirming = confirming === a.relationshipId;
        return (
          <div key={a.relationshipId} className="trader-down-banner" role="alert" aria-live="assertive">
            <div className="trader-down-banner-inner">
              <span className="trader-down-banner-icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                </svg>
              </span>

              <div className="trader-down-banner-text">
                <strong>
                  Trader “{a.traderName}”{a.traderAccountLabel ? ` · ${a.traderAccountLabel}` : ''} is currently offline
                </strong>
                <span>
                  This trader is temporarily unavailable, so their positions aren’t being
                  managed right now{a.openPositions ? ` — you have ${a.openPositions} copied position${a.openPositions === 1 ? '' : 's'} open from this trader` : ''}
                  {a.copierAccountLabel ? ` on ${a.copierAccountLabel}` : ''}.
                </span>
              </div>

              {result ? (
                <div className="trader-down-banner-result">{result}</div>
              ) : isConfirming ? (
                <div className="trader-down-banner-actions">
                  <span className="trader-down-confirm-text">
                    Close {a.openPositions || 'all'} position{a.openPositions === 1 ? '' : 's'} &amp; pause copying?
                  </span>
                  <button className="btn btn-sm btn-danger" disabled={isBusy} onClick={() => closeAll(a)}>
                    {isBusy ? 'Closing…' : 'Yes, close all'}
                  </button>
                  <button className="btn btn-sm btn-neutral" disabled={isBusy} onClick={() => setConfirming(null)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="trader-down-banner-actions">
                  <button className="btn btn-sm btn-danger" onClick={() => setConfirming(a.relationshipId)}>
                    Close all positions
                  </button>
                  <button className="btn btn-sm btn-neutral" onClick={() => keepAndWait(a)}>
                    Keep open &amp; wait
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
